import classNames from "classnames";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { HDTab } from "@common/utils/types";

export enum HDTabVariant {
    Border = "border",
    Highlight = "highlight",
}

/**
 * Reusable Component for tabs in the app
 * @param tabs - Array of tabs
 * @param changeTab - Function to change the tab
 * @param disabled - Whether the tabs are disabled
 * @returns JSX.Element
 */
function HDTabs({
    tabs,
    changeTab,
    disabled,
    paddingVertical = "py-3",
    paddingHorizontal = "",
    fullWidth = false,
    tabRefs,
    showBorder = true,
    variant = HDTabVariant.Border,
    orientation = "horizontal",
    marginTop = "mt-1 sm:mt-2",
    labelClassName = "",
    iconClassName = "",
}: {
    tabs: HDTab[];
    changeTab: (tab: string) => void;
    disabled?: boolean;
    paddingVertical?: string;
    paddingHorizontal?: string;
    fullWidth?: boolean;
    tabRefs?: React.RefObject<HTMLButtonElement | null>[];
    showBorder?: boolean;
    /**
     * Outer top margin. Defaults to the spacing that suits tabs sitting under a page
     * heading; pass "" where the tab strip has to align to its container instead, such
     * as the navbar, where the margin pushes the underline past the bar's own border.
     */
    marginTop?: string;
    /**
     * Extra classes for the tab label. Lets a caller drop to icon-only where the labels
     * will not fit (the navbar at phone widths) without losing them for screen readers.
     */
    labelClassName?: string;
    /**
     * Extra classes for the tab icon. It styles an icon that is already showing - whether one
     * renders at all is `tab.icon` - so it is how a caller pairing this with `labelClassName`
     * sizes the icon up for the widths where it is left carrying the tab on its own.
     */
    iconClassName?: string;
    variant?: HDTabVariant;
    orientation?: "horizontal" | "vertical";
}) {
    const isAnyTabActive = useMemo(() => tabs.some((tab) => tab.isActive), [tabs]);
    const firstEnabledTabIndex = useMemo(() => tabs.findIndex((tab) => !tab.disabled), [tabs]);
    const [focusedTabIndex, setFocusedTabIndex] = useState<number | null>(null);

    useEffect(() => {
        if (focusedTabIndex !== null && focusedTabIndex >= tabs.length) {
            setFocusedTabIndex(null);
        }
    }, [tabs, focusedTabIndex]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
            if (disabled) return;

            const enabledIndices = tabs.map((tab, idx) => (!tab.disabled ? idx : -1)).filter((idx) => idx !== -1);

            const currentEnabledPosition = enabledIndices.indexOf(currentIndex);
            if (currentEnabledPosition === -1) return;

            let targetIndex: number | null = null;

            switch (event.key) {
                case "ArrowRight":
                case "ArrowDown": {
                    event.preventDefault();
                    const nextPosition = (currentEnabledPosition + 1) % enabledIndices.length;
                    targetIndex = enabledIndices[nextPosition];
                    break;
                }
                case "ArrowLeft":
                case "ArrowUp": {
                    event.preventDefault();
                    const prevPosition = (currentEnabledPosition - 1 + enabledIndices.length) % enabledIndices.length;
                    targetIndex = enabledIndices[prevPosition];
                    break;
                }
                case "Home": {
                    event.preventDefault();
                    targetIndex = enabledIndices[0];
                    break;
                }
                case "End": {
                    event.preventDefault();
                    targetIndex = enabledIndices[enabledIndices.length - 1];
                    break;
                }
                case "Enter":
                case " ": {
                    event.preventDefault();
                    const currentTab = tabs[currentIndex];
                    if (!currentTab.isActive) {
                        changeTab(currentTab.id);
                    }
                    return;
                }
            }

            if (targetIndex !== null && targetIndex !== currentIndex) {
                setFocusedTabIndex(targetIndex);
                const targetButton = document.getElementById(`tab-${tabs[targetIndex].id}`);
                targetButton?.focus();
            }
        },
        [tabs, changeTab, disabled],
    );

    const isVertical = orientation === "vertical";
    // Vertical uses the Highlight look (filled pill) regardless of `variant`, because a
    // bottom-border active indicator reads as an underline in a vertical list.
    const useHighlightLook = isVertical || variant === HDTabVariant.Highlight;

    const renderTabButton = (tab: HDTab, index: number, groupHeadingId?: string) => {
        const isFocusable =
            focusedTabIndex !== null
                ? index === focusedTabIndex
                : tab.isActive || (!isAnyTabActive && index === firstEnabledTabIndex);
        return (
            <button
                id={`tab-${tab.id}`}
                disabled={disabled || tab.disabled}
                key={tab.id}
                onClick={() => {
                    setFocusedTabIndex(index);
                    changeTab(tab.id);
                }}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={classNames(
                    isVertical
                        ? tab.isActive
                            ? "border border-gray-200 bg-white font-semibold text-gray-900 cursor-default"
                            : "border border-transparent text-gray-600 hover:bg-white/75 hover:border-gray-200 hover:text-gray-900 cursor-pointer"
                        : tab.isActive
                          ? useHighlightLook
                              ? "bg-max-50 text-max-900 rounded-md border-none cursor-default font-semibold"
                              : "border-max-700 text-max-900 cursor-default font-semibold"
                          : useHighlightLook
                            ? "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-900 cursor-pointer font-semibold"
                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer font-semibold",
                    paddingVertical,
                    " group inline-flex items-center text-sm gap-x-1",
                    isVertical
                        ? "w-full justify-start text-left px-2.5 rounded-md gap-2 whitespace-nowrap transition-colors"
                        : classNames("border-b-2 px-2 sm:px-4", fullWidth ? "w-full justify-center" : "w-auto"),
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-max-700",
                )}
                role="tab"
                aria-selected={tab.isActive}
                aria-current={tab.isActive ? "page" : undefined}
                // Points at the vertical rail's group heading so screen readers get the same
                // "Your practice" / "Connections" / "Account" grouping sighted users see. It is a
                // description rather than part of the accessible name on purpose: the name stays
                // exactly the tab's label. Referencing an aria-hidden element is fine here -
                // aria-describedby uses the referenced node's text even when it is hidden.
                aria-describedby={groupHeadingId}
                tabIndex={isFocusable ? 0 : -1}
                ref={tabRefs ? tabRefs[index] : null}
            >
                {tab?.icon ? (
                    <tab.icon
                        aria-hidden="true"
                        className={classNames(
                            "w-4 h-4 shrink-0 transition-colors",
                            iconClassName,
                            isVertical
                                ? // The vertical rail deliberately mutes its icons against the
                                  // label: they sit in a list where the label carries the row.
                                  // group-hover works because the button carries `group`.
                                  tab.isActive
                                    ? "text-gray-900"
                                    : "text-gray-400 group-hover:text-gray-600"
                                : // Horizontally the icon sits directly beside its label, so any
                                  // difference reads as a mistake rather than as hierarchy. It had
                                  // one: gray-900 against a max-900 label when active, and a step
                                  // light of it when idle and hovered. Inheriting keeps all three
                                  // states in step with whatever the button resolves to.
                                  "text-current",
                        )}
                    />
                ) : null}
                <span
                    className={classNames(
                        "relative text-sm",
                        isVertical ? (tab.isActive ? "font-semibold" : "font-normal") : "font-semibold",
                        isVertical && "truncate",
                        labelClassName,
                    )}
                >
                    {tab.header}

                    {tab?.error && (
                        <div className="absolute top-[5px] right-[-5px] transform translate-x-1/2 -translate-y-1/2 h-2 w-2 bg-red-600 rounded-full" />
                    )}
                </span>

                {tab.suffix}

                {tab?.count !== undefined ? (
                    <span
                        className={classNames(
                            tab.isActive
                                ? "bg-max-50 text-max-900 border border-max-200"
                                : "bg-gray-50 text-gray-700 border border-gray-200",

                            "flex text-center items-center p-2 justify-center rounded-full h-4 w-4 sm:h-6 sm:w-6 text-xs font-semibold",
                        )}
                    >
                        {tab.count}
                    </span>
                ) : null}
            </button>
        );
    };

    if (isVertical) {
        // `group` is set only on the FIRST surviving tab of each group (see Settings.tsx), so
        // carry that heading's id forward to every tab underneath it - otherwise only the first tab
        // of each group would be described, and the rest would announce with no grouping at all.
        let runningGroupHeadingId: string | undefined;
        const groupHeadingIdForTab = tabs.map((tab) => {
            if (tab.group) runningGroupHeadingId = `hdtabs-group-${tab.id}`;
            return runningGroupHeadingId;
        });

        return (
            <div className="flex flex-col gap-0.5 w-full" role="tablist" aria-orientation="vertical">
                {tabs.map((tab, index) => (
                    <Fragment key={tab.id}>
                        {tab.group && (
                            <div
                                id={`hdtabs-group-${tab.id}`}
                                // Kept out of the accessibility tree as an element: a tablist should
                                // contain only tabs. The tabs reach its text via aria-describedby.
                                aria-hidden="true"
                                className="px-2.5 pb-1 pt-4 text-[10px] font-medium uppercase tracking-wider text-gray-400"
                            >
                                {tab.group}
                            </div>
                        )}
                        {renderTabButton(tab, index, groupHeadingIdForTab[index])}
                    </Fragment>
                ))}
            </div>
        );
    }

    return (
        <div className={classNames(marginTop, "flex flex-grow w-full xl:w-auto justify-between")}>
            <div className="sm:block grow flex-shrink-0">
                <div className={classNames("w-full", showBorder && "border-b border-gray-200", paddingHorizontal)}>
                    <div className="-mb-px flex justify-start space-x-2" role="tablist">
                        {/* Explicit arity: a bare `map(renderTabButton)` would pass the array as
                            the third argument, which is now the group heading id. */}
                        {tabs.map((tab, index) => renderTabButton(tab, index))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HDTabs;
